import { useTranslation } from 'react-i18next';
import { useIsFavorite, useToggleFavorite } from '@/hooks/useFavorites';

/**
 * Server-persisted favourite toggle for a location. Lives on the station
 * page's photo and, since the map's location sheet stopped linking to
 * that page (2026-10-23), on the sheet too, so a driver can still save a
 * location from the map. Each host styles the button through `className`;
 * `idleColor` is the heart's colour when not favourited (white over the
 * photo, muted text on the sheet).
 */
export function FavoriteHeart({
  stationId, className, idleColor,
}: { stationId: string; className?: string; idleColor: string }) {
  const { t } = useTranslation('station');
  const { data: favorited, isLoading } = useIsFavorite(stationId);
  const toggleMut = useToggleFavorite();

  const pending = isLoading || toggleMut.isPending;
  const active = Boolean(favorited);

  return (
    <button
      type="button"
      aria-label={active ? t('favorite.remove') : t('favorite.add')}
      aria-pressed={active}
      onClick={() => toggleMut.mutate({ stationId, favorited: active })}
      disabled={pending}
      className={className}
      style={{
        color: active ? 'var(--evi-warn)' : idleColor,
        opacity: pending ? 0.7 : 1,
      }}
      onPointerDown={(e) => { e.currentTarget.style.transform = 'scale(0.88)'; }}
      onPointerUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onPointerLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
    >
      <HeartIcon filled={active} />
    </button>
  );
}

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24" width="18" height="18"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
